import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PeekAutocomplete, PeekInputAdornment, PeekListItem, PeekStack, PeekTextField, PeekTypography, } from "@piiqtechnologies/ui-components";
import { useGoogleMapApi } from "@piiqtechnologies/ui-lib";
import { Search } from "@piiqtechnologies/svg-icons";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import messages from "../messages.i18n";
const REGION_BIAS_RADIUS_METERS = 50000;
export const CommunityAutocompleteInput = ({ field, form, label, placeholder, analyticsSurface = "new_report", regionCenter, includeAddressResults = false, }) => {
    var _a, _b;
    const intl = useIntl();
    const track = useTrackEvent();
    const { mapAPiloaded } = useGoogleMapApi();
    const isGoogleMapsReady = mapAPiloaded || (typeof window !== "undefined" && !!((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places));
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const placesServiceDivRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const mountedRef = useRef(true);
    const isTouched = !!form.touched[field.name];
    const error = isTouched ? form.errors[field.name] : undefined;
    useEffect(() => {
        var _a, _b;
        if (isGoogleMapsReady && ((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places)) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }
    }, [isGoogleMapsReady]);
    useEffect(() => {
        var _a, _b;
        if (isGoogleMapsReady && placesServiceDivRef.current && ((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.places)) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(placesServiceDivRef.current);
        }
    }, [isGoogleMapsReady]);
    const fetchWebsiteForPlace = useCallback((placeId) => {
        if (!placesServiceRef.current)
            return;
        placesServiceRef.current.getDetails({ placeId, fields: ["website"] }, (place, status) => {
            if (!mountedRef.current)
                return;
            if (status === window.google.maps.places.PlacesServiceStatus.OK && (place === null || place === void 0 ? void 0 : place.website)) {
                setOptions((prev) => prev.map((opt) => (opt.place_id === placeId ? Object.assign(Object.assign({}, opt), { website: place.website }) : opt)));
            }
        });
    }, []);
    const fetchPredictions = useCallback((input) => {
        var _a, _b;
        if (!autocompleteServiceRef.current || input.length < 3) {
            setOptions([]);
            return;
        }
        setLoading(true);
        const baseRequest = {
            input,
            componentRestrictions: { country: ["us"] },
        };
        if (regionCenter && ((_b = (_a = window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.Circle)) {
            const bounds = new window.google.maps.Circle({
                center: regionCenter,
                radius: REGION_BIAS_RADIUS_METERS,
            }).getBounds();
            if (bounds)
                baseRequest.locationBias = bounds;
        }
        const searchTypes = includeAddressResults ? [["establishment"], ["address"]] : [["establishment"]];
        let completedCount = 0;
        const allPredictions = [];
        searchTypes.forEach((types) => {
            autocompleteServiceRef.current.getPlacePredictions(Object.assign(Object.assign({}, baseRequest), { types }), (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    predictions.forEach((p) => {
                        if (!allPredictions.some((existing) => existing.place_id === p.place_id)) {
                            allPredictions.push({
                                place_id: p.place_id,
                                description: p.description,
                                structured_formatting: {
                                    main_text: p.structured_formatting.main_text,
                                    secondary_text: p.structured_formatting.secondary_text,
                                },
                                isEstablishment: types.includes("establishment"),
                            });
                        }
                    });
                }
                completedCount++;
                if (completedCount === searchTypes.length) {
                    if (!mountedRef.current)
                        return;
                    setLoading(false);
                    setOptions(allPredictions);
                    track(AnalyticsArea.NewReport, "Community Searched", {
                        queryLength: input.length,
                        resultCount: allPredictions.length,
                        surface: analyticsSurface,
                    });
                    allPredictions.forEach((opt) => fetchWebsiteForPlace(opt.place_id));
                }
            });
        });
    }, [fetchWebsiteForPlace, track, analyticsSurface, regionCenter, includeAddressResults]);
    const handleInputChange = (_event, newValue, reason) => {
        setInputValue(newValue);
        if ((reason === "input" && !newValue) || reason === "clear") {
            form.setFieldValue(field.name, null);
            form.setFieldValue("communityWebsite", "");
            form.setFieldTouched(field.name, true);
        }
        if (reason !== "input")
            return;
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            fetchPredictions(newValue);
        }, 300);
    };
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (debounceTimerRef.current)
                clearTimeout(debounceTimerRef.current);
        };
    }, []);
    const handleSelect = (_event, option) => {
        if (!option || typeof option === "string" || !placesServiceRef.current)
            return;
        placesServiceRef.current.getDetails({
            placeId: option.place_id,
            fields: ["name", "formatted_address", "website"],
        }, (place, status) => {
            var _a;
            if (!mountedRef.current)
                return;
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                const result = {
                    name: place.name || option.structured_formatting.main_text,
                    address: place.formatted_address || option.description,
                    website: place.website || "",
                    isEstablishment: (_a = option.isEstablishment) !== null && _a !== void 0 ? _a : false,
                };
                form.setFieldValue(field.name, result);
                form.setFieldValue("communityWebsite", result.website);
                form.setFieldTouched(field.name, true);
            }
        });
    };
    const getNoOptionsText = () => {
        if (inputValue.length < 3) {
            return intl.formatMessage(messages.noOptions);
        }
        return intl.formatMessage(messages.noResults);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: placesServiceDivRef, style: { display: "none" } }),
        React.createElement(PeekStack, { direction: "column" },
            label && React.createElement(PeekTypography, { variant: "h4" }, label),
            React.createElement(PeekAutocomplete, { freeSolo: true, popupIcon: null, disabled: !isGoogleMapsReady, loading: loading, options: options, inputValue: inputValue, noOptionsText: getNoOptionsText(), getOptionLabel: (option) => typeof option === "string"
                    ? option
                    : `${option.structured_formatting.main_text} — ${option.structured_formatting.secondary_text}`, isOptionEqualToValue: (option, val) => option.place_id === val.place_id, onChange: handleSelect, onInputChange: handleInputChange, filterOptions: (x) => x, renderOption: (props, option) => (React.createElement(PeekListItem, Object.assign({}, props, { key: option.place_id }),
                    React.createElement(PeekStack, { direction: "column", alignItems: "baseline" },
                        React.createElement(PeekTypography, { variant: "body1", fontWeight: "fontWeightMedium" }, option.structured_formatting.main_text),
                        React.createElement(PeekTypography, { variant: "body2", color: "info.main" }, option.structured_formatting.secondary_text)))), renderInput: (params) => (React.createElement(PeekTextField, Object.assign({}, params, { fullWidth: true, placeholder: placeholder, error: !!error, helperText: error, onBlur: () => form.setFieldTouched(field.name, true), InputProps: Object.assign(Object.assign({}, params.InputProps), { startAdornment: (React.createElement(PeekInputAdornment, { position: "start" },
                            React.createElement(Search, { width: 20, height: 20 }))) }), sx: {
                        ".MuiOutlinedInput-notchedOutline": {
                            borderRadius: "10px",
                            borderColor: "grey.100",
                            transition: "border-color 0.2s ease-in-out",
                        },
                    } }))) }))));
};
export default CommunityAutocompleteInput;
